import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Card, Row, Col, Statistic, Skeleton, Tag, Space, Select } from 'antd';
import {
  ApiOutlined,
  RiseOutlined,
  FallOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import ApiVolumeIntervalChart from './ApiVolumeIntervalChart';
import { fetchApiVolumeByInterval } from '../../store/actions/analytics';

import { Line, Column, Pie, DualAxes } from '@ant-design/charts';

const { Option } = Select;

/* -----------------------------------------------------
   SAFE FORMATTER FOR AXES & CHART VALUES (prevents crash)
------------------------------------------------------*/
const safe = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
};

const KPIDashboard = ({ logs, summaryData, loading }) => {
  const dispatch = useDispatch();
  const [timeRange, setTimeRange] = useState('day'); // 'hour', 'day', 'week', 'month', 'year'
  const { filters } = useSelector((state) => state.analytics);

  useEffect(() => {
    const now = new Date();
    let start_date;
    let interval; // in seconds

    switch (timeRange) {
      case 'hour':
        start_date = new Date(now.getTime() - 60 * 60 * 1000);
        interval = 60 * 5; // 5 minutes
        break;
      case 'week':
        start_date = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        interval = 3600 * 24; // 1 day
        break;
      case 'month':
        start_date = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        interval = 3600 * 24; // 1 day
        break;
      case 'year':
        start_date = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        interval = 3600 * 24 * 30; // ~1 month
        break;
      case 'day':
      default:
        start_date = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        interval = 3600; // 1 hour
        break;
    }

    const end_date = now;
    const formatDate = (date) => date.toISOString().split('T')[0];

    const fetchFilters = {
      ...filters,
      dates: [formatDate(start_date), formatDate(end_date)],
    };

    dispatch(fetchApiVolumeByInterval(interval, fetchFilters));
  }, [timeRange, dispatch, filters]);

  /* -----------------------------------------------------
      PROCESS LOG DATA
  ------------------------------------------------------*/
  const chartData = useMemo(() => {
    if (summaryData) {
      const { system_health, top_users, api_usage } = summaryData;

      // Data for charts that still require logs processing
      const dailyData = {};
      const hourlyData = Array(24).fill(0).map((_, i) => ({ hour: i, count: 0 }));
      if (logs) {
        logs.forEach((log) => {
          const date = new Date(log.created_at);
          if (isNaN(date)) return;

          const dateKey = date.toISOString().split('T')[0];
          const hour = date.getHours();

          if (!dailyData[dateKey]) {
            dailyData[dateKey] = { date: dateKey, requests: 0, success: 0, error: 0 };
          }
          dailyData[dateKey].requests++;
          hourlyData[hour].count++;

          if (log.status === 'success' || log.response_code === 200) {
            dailyData[dateKey].success++;
          } else {
            dailyData[dateKey].error++;
          }
        });
      }

      return {
        dailyData: Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date)),
        hourlyData,
        statusData: [
          { type: 'Success', value: system_health?.SUCCESS || 0 },
          { type: 'Error', value: system_health?.FAILURE || 0 },
        ],
        topUsers: top_users?.map(u => ({ user: u.display_name, requests: u.count })) || [],
        topEndpoints: api_usage?.top_endpoints?.map(e => ({ endpoint: e.endpoint, requests: e.count })) || [],
        totalRequests: system_health?._total || 0,
        successRate: (system_health?._success_rate || 0).toFixed(2),
      };
    }

    // Fallback to original implementation if summaryData is not present
    if (!logs || logs.length === 0) return null;

    const dailyData = {};
    const hourlyData = Array(24).fill(0).map((_, i) => ({ hour: i, count: 0 }));
    const statusCounts = { success: 0, error: 0 };
    const userActivity = {};
    const endpointUsage = {};

    logs.forEach((log) => {
      const date = new Date(log.created_at);
      if (isNaN(date)) return;
      const dateKey = date.toISOString().split('T')[0];
      const hour = date.getHours();

      if (!dailyData[dateKey]) {
        dailyData[dateKey] = { date: dateKey, requests: 0, success: 0, error: 0 };
      }
      dailyData[dateKey].requests++;
      hourlyData[hour].count++;

      if (log.status === 'success' || log.response_code === 200) {
        dailyData[dateKey].success++;
        statusCounts.success++;
      } else {
        dailyData[dateKey].error++;
        statusCounts.error++;
      }

      const userName = log.user_name || 'Unknown';
      userActivity[userName] = (userActivity[userName] || 0) + 1;
      const endpoint = log.endpoint || log.action || 'Unknown';
      endpointUsage[endpoint] = (endpointUsage[endpoint] || 0) + 1;
    });

    return {
      dailyData: Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date)),
      hourlyData,
      statusData: [
        { type: 'Success', value: statusCounts.success },
        { type: 'Error', value: statusCounts.error },
      ],
      topUsers: Object.entries(userActivity)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ user: name, requests: count })),
      topEndpoints: Object.entries(endpointUsage)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([endpoint, count]) => ({ endpoint, requests: count })),
      totalRequests: logs.length,
      successRate: logs.length > 0 ? ((statusCounts.success / logs.length) * 100).toFixed(2) : "0.00",
    };
  }, [logs, summaryData]);

  const { apiVolumeInterval, loadingApiVolumeInterval } = useSelector(
    (state) => state.analytics
  );

  if (loading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (!chartData) return <div style={{ padding: 24, textAlign: 'center' }}>No data</div>;

  /* -----------------------------------------------------
      TREND CALCULATION
  ------------------------------------------------------*/
  const midPoint = Math.floor(chartData.dailyData.length / 2);
  const firstHalf = chartData.dailyData.slice(0, midPoint)
    .reduce((sum, d) => sum + d.requests, 0);
  const secondHalf = chartData.dailyData.slice(midPoint)
    .reduce((sum, d) => sum + d.requests, 0);

  const trend = secondHalf > firstHalf ? 'up' : 'down';
  const trendPercent = firstHalf > 0
    ? (((secondHalf - firstHalf) / firstHalf) * 100).toFixed(1)
    : 0;

  /* -----------------------------------------------------
      DAILY LINE CHART
  ------------------------------------------------------*/
  const dailyLineConfig = {
    data: chartData.dailyData,
    xField: 'date',
    yField: 'requests',
    smooth: true,
    color: '#1890ff',
    point: { size: 4, shape: 'circle' },
    xAxis: {
      label: {
        formatter: (v) => {
          const d = new Date(v);
          return isNaN(d) ? v : `${d.getMonth() + 1}/${d.getDate()}`;
        },
      },
    },
    yAxis: {
      label: { formatter: safe },
    },
    tooltip: {
      customContent: (title, items) => {
        if (!items || items.length === 0) return 'No data';
        const { requests } = items[0].data;
        return `<div style="padding:6px;">Requests: ${safe(requests)}</div>`;
      },
    },
  };

  /* -----------------------------------------------------
      SUCCESS / ERROR TREND (DualAxes)
  ------------------------------------------------------*/
  const dualAxesConfig = {
    data: [chartData.dailyData, chartData.dailyData],
    xField: 'date',
    yField: ['success', 'error'],
    geometryOptions: [
      { geometry: 'line', color: '#52c41a' },
      { geometry: 'line', color: '#ff4d4f', lineStyle: { lineDash: [3, 4] } },
    ],
    xAxis: {
      label: {
        formatter: (v) => {
          const d = new Date(v);
          return isNaN(d) ? v : `${d.getMonth() + 1}/${d.getDate()}`;
        },
      },
    },
    tooltip: {
      customContent: (title, items) => {
        if (!items?.length) return 'No data';
        const val = items[0].data?.success ?? items[0].data?.error ?? 0;
        return `<div style="padding:6px;">${safe(val)}</div>`;
      },
    },
  };

  /* -----------------------------------------------------
      HOURLY COLUMN CHART (FIXED)
  ------------------------------------------------------*/
  const hourlyColumnConfig = {
    data: chartData.hourlyData,
    xField: 'hour',
    yField: 'count',
    color: '#722ed1',
    columnStyle: { radius: [4, 4, 0, 0] },
    xAxis: {
      label: { formatter: (v) => `${safe(v)}:00` },
    },
    tooltip: {
      customContent: (title, items) => {
        if (!items?.length) return 'No data';
        const { hour, count } = items[0].data ?? {};
        return `
          <div style="padding:6px;">
            <strong>${hour}:00 - ${hour}:59</strong><br/>
            Requests: ${safe(count)}
          </div>
        `;
      },
    },
  };

  /* -----------------------------------------------------
      PIE CHART (FIXED)
  ------------------------------------------------------*/
  const pieConfig = {
    data: chartData.statusData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    innerRadius: 0.6,
    label: {
      offset: '-30%',
      formatter: (item) => safe(item?.value),
    },
    statistic: {
      title: { content: 'Success Rate' },
      content: {
        content: `${chartData.successRate}%`,
        style: { fontSize: 24, fontWeight: 'bold' },
      },
    },
    interactions: [{ type: 'element-active' }],
  };

  /* -----------------------------------------------------
      TOP USERS
  ------------------------------------------------------*/
  const topUsersConfig = {
    data: chartData.topUsers,
    xField: 'requests',
    yField: 'user',
    color: '#13c2c2',
    xAxis: { label: { formatter: safe } },
  };

  /* -----------------------------------------------------
      TOP ENDPOINTS
  ------------------------------------------------------*/
  const topEndpointsConfig = {
    data: chartData.topEndpoints,
    xField: 'requests',
    yField: 'endpoint',
    color: '#fa8c16',
    xAxis: { label: { formatter: safe } },
  };

  /* -----------------------------------------------------
      RENDER DASHBOARD
  ------------------------------------------------------*/
  return (
    <div>
      {/* SUMMARY CARDS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background:'linear-gradient(135deg,#667eea,#764ba2)' }}>
            <Statistic
              title={<span style={{ color:'rgba(255,255,255,.85)' }}>Total Requests</span>}
              value={chartData.totalRequests}
              valueStyle={{ color:'#fff' }}
              prefix={<ApiOutlined />}
              suffix={
                <Space>
                  {trend === 'up' ? <RiseOutlined /> : <FallOutlined />}
                  <span>{trendPercent}%</span>
                </Space>
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background:'linear-gradient(135deg,#f093fb,#f5576c)' }}>
            <Statistic
              title={<span style={{ color:'rgba(255,255,255,.85)' }}>Success Rate</span>}
              value={chartData.successRate}
              valueStyle={{ color:'#fff' }}
              prefix={<CheckCircleOutlined />}
              suffix="%"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background:'linear-gradient(135deg,#4facfe,#00f2fe)' }}>
            <Statistic
              title={<span style={{ color:'rgba(255,255,255,.85)' }}>Successful</span>}
              value={chartData.statusData[0].value}
              valueStyle={{ color:'#fff' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background:'linear-gradient(135deg,#fa709a,#fee140)' }}>
            <Statistic
              title={<span style={{ color:'rgba(255,255,255,.85)' }}>Failed</span>}
              value={chartData.statusData[1].value}
              valueStyle={{ color:'#fff' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* MAIN CHARTS */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Daily Request Volume" bordered={false}>
            <Line {...dailyLineConfig} height={300} />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Request Status Distribution" bordered={false}>
            <Pie {...pieConfig} height={300} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Success vs Error Trends" bordered={false}>
            <DualAxes {...dualAxesConfig} height={280} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Hourly Request Distribution" bordered={false}>
            <Column {...hourlyColumnConfig} height={280} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Top 5 Active Users" bordered={false}>
            <Column {...topUsersConfig} height={280} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Top 5 Endpoints" bordered={false}>
            <Column {...topEndpointsConfig} height={280} />
          </Card>
        </Col>
      </Row>

      {/* NEW API VOLUME INTERVAL CHART */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="API Request Volume (Grouped)">
            <Select value={timeRange} onChange={setTimeRange} style={{ width: 120, marginBottom: 16 }}>
              <Option value="hour">Last Hour</Option>
              <Option value="day">Last Day</Option>
              <Option value="week">Last Week</Option>
              <Option value="month">Last Month</Option>
              <Option value="year">Last Year</Option>
            </Select>
            <ApiVolumeIntervalChart
              data={apiVolumeInterval}
              loading={loadingApiVolumeInterval}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default KPIDashboard;
